/**
 * Networks Store
 * State management for Networks Management page
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { NetworkFilters, SyncProgress } from '../types/networks.types';

interface NetworksStore {
  // Filters
  filters: NetworkFilters;
  setFilters: (filters: Partial<NetworkFilters>) => void;
  resetFilters: () => void;

  // Connection Wizard
  wizardOpen: boolean;
  wizardStep: 1 | 2 | 3;
  openWizard: () => void;
  closeWizard: () => void;
  nextStep: () => void;
  prevStep: () => void;
  resetWizard: () => void;

  // Sync Progress
  syncProgress: Record<string, SyncProgress>;
  setSyncProgress: (networkId: string, progress: SyncProgress) => void;
  clearSyncProgress: (networkId: string) => void;

  // Bulk Actions
  selectedNetworks: string[];
  toggleNetworkSelection: (networkId: string) => void;
  selectAllNetworks: (networkIds: string[]) => void;
  clearSelection: () => void;
}

const DEFAULT_FILTERS: NetworkFilters = {
  search: '',
  status: 'all',
  platform: 'all',
  sortBy: 'last_sync',
  sortOrder: 'desc'
};

export const useNetworksStore = create<NetworksStore>()(  
  persist(
    (set, get) => ({
      // Filters
      filters: DEFAULT_FILTERS,
      setFilters: (newFilters) => set((state) => ({
        filters: { ...state.filters, ...newFilters }
      })),
      resetFilters: () => set({ filters: DEFAULT_FILTERS }),

      // Connection Wizard
      wizardOpen: false,
      wizardStep: 1,
      openWizard: () => set({ wizardOpen: true, wizardStep: 1 }),
      closeWizard: () => set({ wizardOpen: false }),
      nextStep: () => set((state) => ({
        wizardStep: Math.min(state.wizardStep + 1, 3) as 1 | 2 | 3
      })),
      prevStep: () => set((state) => ({
        wizardStep: Math.max(state.wizardStep - 1, 1) as 1 | 2 | 3
      })),
      resetWizard: () => set({ wizardOpen: false, wizardStep: 1 }),

      // Sync Progress
      syncProgress: {},
      setSyncProgress: (networkId, progress) => set((state) => ({
        syncProgress: {
          ...state.syncProgress,
          [networkId]: progress
        }
      })),
      clearSyncProgress: (networkId) => set((state) => {
        const newProgress = { ...state.syncProgress };
        delete newProgress[networkId];
        return { syncProgress: newProgress };
      }),

      // Bulk Actions
      selectedNetworks: [],
      toggleNetworkSelection: (networkId) => set((state) => ({
        selectedNetworks: state.selectedNetworks.includes(networkId)
          ? state.selectedNetworks.filter(id => id !== networkId)
          : [...state.selectedNetworks, networkId]
      })),
      selectAllNetworks: (networkIds) => set({ selectedNetworks: networkIds }),
      clearSelection: () => set({ selectedNetworks: [] })
    }),
    {
      name: 'adwstats-networks-storage',
      partialize: (state) => ({
        filters: state.filters
      })
    }
  )
);
